import { SITE_ORIGIN, SITE_OG_IMAGE } from './config';
import {
  articleContents,
  articleContentsB,
  type ArticleContent,
} from '@/mocks/articleContents';

const allContents: Record<string, ArticleContent> = {
  ...articleContents,
  ...articleContentsB,
};

export type PageType = 'home' | 'articles-list' | 'article' | 'page' | 'notfound';

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  type: 'website' | 'article';
  imageUrl?: string;
  noindex?: boolean;
  pageType: PageType;
  articleSlug?: string;
}

const HOME_TITLE =
  'CSSBuy Spreadsheet: Encontre Produtos, Links e Guias da CSSBuy';
const HOME_DESC =
  'CSSBuy Spreadsheet: guia independente com links de produtos, categorias, avaliacoes, frete, warehouse, cupons e dicas de seguranca. Recurso em portugues para pesquisar antes de comprar.';
const HOME_KEYWORDS =
  'CSSBuy Spreadsheet, CSSBuy Review, CSSBuy Shipping, CSSBuy Coupon, CSSBuy Warehouse, CSSBuy Legit, CSSBuy Guide';

function truncate(text: string, max = 158): string {
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max).trimEnd()}…`;
}

function buildHome(path: string): SeoData {
  return {
    title: HOME_TITLE,
    description: HOME_DESC,
    keywords: HOME_KEYWORDS,
    canonicalUrl: `${SITE_ORIGIN}${path === '/' ? '/' : path}`,
    type: 'website',
    imageUrl: SITE_OG_IMAGE,
    pageType: 'home',
  };
}

function buildArticleSeo(content: ArticleContent): SeoData {
  return {
    title: `${content.title} | CSSSpreadsheetBR`,
    description: truncate(content.quickAnswer),
    keywords: `CSSBuy Spreadsheet, ${content.category}, CSSBuy Guide, CSSBuy`,
    canonicalUrl: `${SITE_ORIGIN}/pt/articles/${content.slug}`,
    type: 'article',
    imageUrl: content.imageUrl,
    pageType: 'article',
    articleSlug: content.slug,
  };
}

function buildNotFound(): SeoData {
  return {
    title: 'Pagina nao encontrada | CSSSpreadsheetBR',
    description:
      'A pagina que voce procura nao existe. Explore o CSSBuy Spreadsheet e encontre produtos, links e guias.',
    keywords: 'CSSBuy Spreadsheet',
    canonicalUrl: SITE_ORIGIN,
    type: 'website',
    noindex: true,
    pageType: 'notfound',
  };
}

function buildPage(
  path: string,
  title: string,
  description: string,
  keywords: string,
): SeoData {
  return {
    title,
    description,
    keywords,
    canonicalUrl: `${SITE_ORIGIN}${path}`,
    type: 'website',
    pageType: 'page',
  };
}

export function resolveSeoData(pathname: string): SeoData {
  const raw = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');

  const articleMatch = raw.match(/^\/pt\/articles\/([^/]+)$/);
  if (articleMatch) {
    const content = allContents[articleMatch[1]];
    if (content) {
      return buildArticleSeo(content);
    }
    return buildNotFound();
  }

  if (raw === '/' || raw === '/en' || raw === '/de') {
    return buildHome(raw);
  }

  if (raw === '/pt/articles') {
    return buildPage(
      '/pt/articles',
      'Artigos e Guias CSSBuy Spreadsheet | CSSSpreadsheetBR',
      'Artigos, guias e recursos sobre CSSBuy Spreadsheet para pesquisar, comparar e comprar com seguranca. Shipping, QC, warehouse, cupons e mais.',
      'CSSBuy Spreadsheet, CSSBuy Guide, CSSBuy Shipping, CSSBuy QC, CSSBuy Warehouse',
    );
  }

  if (raw === '/about') {
    return buildPage(
      '/about',
      'Sobre o CSSSpreadsheetBR | CSSBuy Spreadsheet',
      'Conheca o CSSSpreadsheetBR, um recurso independente sobre CSSBuy Spreadsheet, produtos, links e guias de compra.',
      'CSSBuy Spreadsheet, CSSSpreadsheetBR, sobre, recurso independente',
    );
  }

  if (raw === '/editorial-policy') {
    return buildPage(
      '/editorial-policy',
      'Editorial Policy | CSSSpreadsheetBR',
      'Politica editorial do CSSSpreadsheetBR: independencia, precisao e conteudo original sobre CSSBuy Spreadsheet.',
      'CSSBuy Spreadsheet, Editorial Policy, CSSSpreadsheetBR',
    );
  }

  if (raw === '/privacy-policy') {
    return buildPage(
      '/privacy-policy',
      'Privacy Policy | CSSSpreadsheetBR',
      'Politica de privacidade do CSSSpreadsheetBR, um site informativo independente sobre CSSBuy Spreadsheet.',
      'CSSBuy Spreadsheet, Privacy Policy, CSSSpreadsheetBR',
    );
  }

  if (raw === '/terms') {
    return buildPage(
      '/terms',
      'Terms of Service | CSSSpreadsheetBR',
      'Termos de uso do CSSSpreadsheetBR, recurso independente sobre CSSBuy Spreadsheet.',
      'CSSBuy Spreadsheet, Terms of Service, CSSSpreadsheetBR',
    );
  }

  return buildNotFound();
}